import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import invoiceService from './invoiceService';
import { toast } from 'react-toastify';

const initialState = {
  invoices: [],
  invoice: null,
  tiposComprobante: {},
  condicionesIVA: {},
  loading: false,
  error: null,
  success: false,
  message: ''
};

// Generar factura desde venta
export const generateInvoiceFromSale = createAsyncThunk(
  'invoice/generateFromSale',
  async (data, thunkAPI) => {
    try {
      const response = await invoiceService.generateInvoiceFromSale(data);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Error al generar factura';
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Generar factura manual
export const generateManualInvoice = createAsyncThunk(
  'invoice/generateManual',
  async (data, thunkAPI) => {
    try {
      const response = await invoiceService.generateManualInvoice(data);
      toast.success('✅ Factura generada exitosamente');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Error al generar factura';
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Obtener todas las facturas
export const getInvoices = createAsyncThunk(
  'invoice/getAll',
  async (_, thunkAPI) => {
    try {
      return await invoiceService.getInvoices();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Error al obtener facturas';
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Obtener factura por ID
export const getInvoiceById = createAsyncThunk(
  'invoice/getById',
  async (id, thunkAPI) => {
    try {
      return await invoiceService.getInvoiceById(id);
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Error al obtener factura';
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Anular factura
export const anularInvoice = createAsyncThunk(
  'invoice/anular',
  async (id, thunkAPI) => {
    try {
      const response = await invoiceService.anularInvoice(id);
      toast.success(response?.message || '✅ Factura anulada');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Error al anular factura';
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Obtener tipos de comprobante
export const getTiposComprobante = createAsyncThunk(
  'invoice/getTiposComprobante',
  async (_, thunkAPI) => {
    try {
      return await invoiceService.getTiposComprobante();
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Obtener condiciones de IVA
export const getCondicionesIVA = createAsyncThunk(
  'invoice/getCondicionesIVA',
  async (_, thunkAPI) => {
    try {
      return await invoiceService.getCondicionesIVA();
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    reset: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = '';
    }
  },
  extraReducers: (builder) => {
    builder
      // Generate from sale
      .addCase(generateInvoiceFromSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateInvoiceFromSale.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.invoice = action.payload.invoice;
        state.invoices.unshift(action.payload.invoice);
        state.message = action.payload.message;
      })
      .addCase(generateInvoiceFromSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Generate manual
      .addCase(generateManualInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateManualInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.invoice = action.payload.invoice;
        state.invoices.unshift(action.payload.invoice);
        state.message = action.payload.message;
      })
      .addCase(generateManualInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get all
      .addCase(getInvoices.pending, (state) => {
        state.loading = true;
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get by ID
      .addCase(getInvoiceById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getInvoiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload;
      })
      .addCase(getInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Anular
      .addCase(anularInvoice.fulfilled, (state, action) => {
        state.success = true;
        const index = state.invoices.findIndex(inv => inv._id === action.payload.invoice._id);
        if (index !== -1) {
          state.invoices[index] = action.payload.invoice;
        }
        // Si vino la Nota de Crédito creada, agregarla a la lista si no existe
        const creditNote = action.payload.creditNote;
        if (creditNote && !state.invoices.some(inv => inv._id === creditNote._id)) {
          state.invoices.unshift(creditNote);
        }
      })
      
      // Tipos de comprobante
      .addCase(getTiposComprobante.fulfilled, (state, action) => {
        state.tiposComprobante = action.payload;
      })
      
      // Condiciones IVA
      .addCase(getCondicionesIVA.fulfilled, (state, action) => {
        state.condicionesIVA = action.payload;
      });
  }
});

export const { reset } = invoiceSlice.actions;
export default invoiceSlice.reducer;
